import {
  Box,
  Button,
  CardContent,
  DialogActions,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Label } from 'src/components';
import {
  AlarmPopupCard,
  AlarmPopupDialog,
  DialogContentWithCustomScroll,
  TableContainerWithCustomScroll,
} from '../style';
import { InputHTMLAttributes, useState } from 'react';
import {
  formatSecondsToDhms,
  formatUTCDateToLocal,
  upperSnakeCaseToTitleCase,
} from 'src/utilities';
import { Notification } from '../../types/notification';
import { SiteInterface } from 'src/features/sites/types/site';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { useNavigate } from 'react-router';
import { SITES_DASHBOARD_PATH } from 'src/routes/config';
import { getPriorityColor, renderTitleIcon } from '../../utils';
import { CustomPaperProps, PopupProps } from '../../types';
import {
  DetailsCellLabel,
  DetailsCellValue,
  FooterStack,
  NotificationsCellLabel,
  NotificationsCellValue,
  NotificationsHeaderCellLabel,
  NotificationsHeaderCellValue,
} from './style';
import { AlarmsService } from 'src/services/alarms/alarms.service';
import { useAuthentication } from 'src/features/authentication/context';
import { ConditionsBox } from '../conditionsBox';
import { LastEditedInfoBox } from '../lastEditedInfoBox';
import { AccordionDeleteAlarmRule } from 'src/features/alarm-rules-details/components/AccordionDeleteAlarmRule';
import { AlertDialogDeleteAlarmRule } from 'src/features/alarm-rules-details/components/AlertDialog';
import { useGetAlarmPopupNotifications } from 'src/hooks/apiHooks/useGetAlarmPopupNotifications';
import { useGetAlarmPopupInfo } from 'src/hooks/apiHooks/useGetAlarmPopupInfo';
import { useToggle } from 'src/hooks/useToggle';
import { SitesService } from 'src/services/sites/sites.service';
import { ArrowTopRightIcon } from 'src/assets/icons/common/ArrowTopRightIcon';

interface CustomInputAttributes extends InputHTMLAttributes<HTMLInputElement> {
  'data-sm'?: string;
}

export const DesktopAlarmPopUp = ({
  open,
  onClose,
  customerId,
  siteId,
  alarmData,
  onAcknowledged,
  setRefreshTrigger,
  handleDeleteAlarmEvent,
  isDeleteAlarmEventAllowed,
}: PopupProps) => {
  const theme = useTheme();
  const { saveSite } = useSite();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const { idToken } = useAuthentication();
  const { state, onToggle } = useToggle();

  const { alarmPopupInfo } = useGetAlarmPopupInfo({
    customerId: customerId!,
    siteId: alarmData.siteId,
    eventId: alarmData.id,
    isOpen: open,
  });
  const { notifications } = useGetAlarmPopupNotifications({
    customerId: customerId!,
    eventId: alarmData.id,
    isOpen: open,
  });

  const onAcknowledgeAndClose = () => {
    AlarmsService.acknowledgeAlarm(customerId, siteId, alarmPopupInfo.id, idToken)
      .then((response) => {
        onAcknowledged && onAcknowledged(response);
        setRefreshTrigger(true);
      })
      .catch((error) => {
        console.error(error);
      });
    setAcknowledged(false);
    onClose();
  };

  const handleGoToSiteClick = () => {
    SitesService.getById({ customerId: customerId, siteId: siteId })
      .then((data: SiteInterface) => {
        saveSite(data);
      })
      .catch((error) => {
        console.error(error);
      });
    navigate(SITES_DASHBOARD_PATH + `?customerId=${customerId}&siteId=${siteId}`);
  };

  const customSwitchProps: CustomInputAttributes = {
    'data-sm': 'acknowledge-button',
  };

  const customPaperProps: CustomPaperProps = {
    'data-sm': 'alarm-popup',
  };
  const onHandleClose = () => {
    setAcknowledged(false);
    onClose();
  };

  return (
    <AlarmPopupDialog
      open={open}
      onClose={onHandleClose}
      maxWidth="lg"
      sx={{ width: '100%' }}
      disableRestoreFocus
      disableEnforceFocus
      PaperProps={{
        ...customPaperProps,
      }}
    >
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default }}>
        <Stack flexDirection={'row'} justifyContent={'space-between'}>
          <Stack flexDirection={'row'} alignItems={'center'}>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              data-sm={`alarm-${alarmPopupInfo?.priority}`}
            >
              {renderTitleIcon(alarmPopupInfo, theme)}
            </Box>
            <Typography variant="h6" sx={{ ml: '16px', mr: '8px' }}>
              Alarm - {upperSnakeCaseToTitleCase(alarmPopupInfo?.priority || '')}
            </Typography>
            <Label
              variant="filled"
              color={getPriorityColor(alarmPopupInfo?.priority, alarmPopupInfo?.status)}
            >
              {alarmPopupInfo?.status}
            </Label>
          </Stack>
          <Button
            variant="outlined"
            endIcon={<ArrowTopRightIcon />}
            onClick={handleGoToSiteClick}
            data-sm={'go-to-site-button'}
          >
            Go to Site
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContentWithCustomScroll sx={{ backgroundColor: theme.palette.background.default }}>
        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Site Details:</Typography>
            <TableContainer sx={{ mt: '12px' }}>
              <Table>
                <TableBody>
                  <TableRow data-sm="site-name">
                    <DetailsCellLabel align="left">Site Name:</DetailsCellLabel>
                    <DetailsCellValue align="left" data-sm={alarmPopupInfo?.siteName}>
                      <strong>{alarmPopupInfo?.siteName}</strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="assets">
                    <DetailsCellLabel align="left">Assets:</DetailsCellLabel>
                    <DetailsCellValue align="left" data-sm="assets-names">
                      {alarmPopupInfo?.assets?.map((asset, i) => {
                        const isLastElement = alarmPopupInfo?.assets?.length === i + 1;
                        return (
                          <strong key={asset.id} data-sm={asset?.name}>
                            {asset?.name ?? '-'}
                            {isLastElement ? '.' : ', '}
                          </strong>
                        );
                      })}
                      {!alarmPopupInfo?.assets && (
                        <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                          No assets assigned to this event
                        </Typography>
                      )}
                    </DetailsCellValue>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </AlarmPopupCard>

        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Event Details:</Typography>
            <TableContainer sx={{ mt: '12px' }}>
              <Table>
                <TableBody>
                  <TableRow data-sm="alarm-title">
                    <DetailsCellLabel align="left">Alarm Title:</DetailsCellLabel>
                    <DetailsCellValue align="left" data-sm={alarmPopupInfo?.title}>
                      <strong>{alarmPopupInfo?.title}</strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="start-date">
                    <DetailsCellLabel align="left">Start (date / time):</DetailsCellLabel>
                    <DetailsCellValue
                      align="left"
                      data-sm={
                        alarmPopupInfo?.startDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.startDate)
                          : '-'
                      }
                    >
                      <strong>
                        {alarmPopupInfo?.startDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.startDate)
                          : '-'}
                      </strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="end-date">
                    <DetailsCellLabel align="left">End (date / time):</DetailsCellLabel>
                    <DetailsCellValue
                      align="left"
                      data-sm={
                        alarmPopupInfo?.endDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.endDate)
                          : '-'
                      }
                    >
                      <strong>
                        {alarmPopupInfo?.endDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.endDate)
                          : '-'}
                      </strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="duration">
                    <DetailsCellLabel align="left">Duration:</DetailsCellLabel>
                    <DetailsCellValue
                      align="left"
                      data-sm={
                        +alarmPopupInfo?.durationInSeconds > 0
                          ? formatSecondsToDhms(+alarmPopupInfo.durationInSeconds)
                          : '-'
                      }
                    >
                      <strong>
                        {+alarmPopupInfo?.durationInSeconds > 0
                          ? formatSecondsToDhms(+alarmPopupInfo?.durationInSeconds)
                          : '-'}
                      </strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="description">
                    <DetailsCellLabel align="left">Description:</DetailsCellLabel>
                    <DetailsCellValue align="left" data-sm={alarmPopupInfo?.description ?? '-'}>
                      <strong>{alarmPopupInfo?.description ?? '-'}</strong>
                    </DetailsCellValue>
                  </TableRow>
                  <TableRow data-sm="acknowledgement">
                    <DetailsCellLabel align="left">
                      <FormControlLabel
                        value="end"
                        control={
                          <Switch
                            checked={alarmPopupInfo?.acknowledgement ? true : acknowledged}
                            disabled={!!alarmPopupInfo?.acknowledgement}
                            color="primary"
                            onChange={(event, checked) => {
                              setAcknowledged(checked);
                            }}
                            inputProps={{ ...customSwitchProps }}
                          />
                        }
                        label={
                          !!alarmPopupInfo?.acknowledgement || acknowledged
                            ? 'Acknowledged'
                            : 'Acknowledge'
                        }
                        labelPlacement="end"
                      />
                    </DetailsCellLabel>
                    {alarmPopupInfo?.acknowledgement ? (
                      <DetailsCellValue align="left" sx={{ verticalAlign: 'inherit' }}>
                        Acknowledged by:{' '}
                        <strong>
                          {alarmPopupInfo?.acknowledgement?.firstName}{' '}
                          {alarmPopupInfo?.acknowledgement?.lastName},{' '}
                          {formatUTCDateToLocal(false, alarmPopupInfo?.acknowledgement?.timestamp)}
                        </strong>
                      </DetailsCellValue>
                    ) : null}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </AlarmPopupCard>

        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Notifications:</Typography>
            <TableContainerWithCustomScroll sx={{ mt: '12px', maxHeight: '125px' }}>
              <Table>
                {notifications?.length > 0 && (
                  <TableHead sx={{ position: 'sticky', top: '0', left: '0', zIndex: '10' }}>
                    <TableRow>
                      <NotificationsHeaderCellLabel theme={theme} align="left">
                        Time
                      </NotificationsHeaderCellLabel>
                      <NotificationsHeaderCellValue theme={theme} align="left">
                        Receivers
                      </NotificationsHeaderCellValue>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody data-sm="notifications-table-body">
                  {notifications?.map((notification: Notification) => (
                    <TableRow key={notification?.id ?? notification?.createdAt}>
                      <NotificationsCellLabel align="left">
                        <strong>{formatUTCDateToLocal(false, notification?.createdAt)}</strong>
                      </NotificationsCellLabel>
                      <NotificationsCellValue align="left">
                        <strong>{notification?.firstName + ' ' + notification?.lastName}</strong>
                      </NotificationsCellValue>
                    </TableRow>
                  ))}
                  {!notifications?.length && (
                    <Typography sx={{ fontSize: '14px' }} data-sm="notifications-message">
                      No notifications have been sent for this event
                    </Typography>
                  )}
                </TableBody>
              </Table>
            </TableContainerWithCustomScroll>
          </CardContent>
        </AlarmPopupCard>

        <ConditionsBox
          clearingInfo={alarmPopupInfo?.clearingInfo}
          triggerInfo={alarmPopupInfo?.triggerInfo}
          type={alarmPopupInfo?.type}
        />
        {isDeleteAlarmEventAllowed && (
          <Box sx={{ padding: ' 0 1rem 1rem 0.3rem' }}>
            <AccordionDeleteAlarmRule
              title="Alarm event options"
              handleClick={onToggle}
              description="Clicking 'Delete' button will permanently delete current alarm event."
            />
          </Box>
        )}
        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={{ color: theme.palette.grey[600] }}>
            Alarm ID: {alarmPopupInfo?.id}
          </Typography>
        </Box>
      </DialogContentWithCustomScroll>
      <Divider />
      <DialogActions
        data-sm="alarm-popup-footer"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <FooterStack>
          <Box>
            <LastEditedInfoBox
              updatedBy={alarmPopupInfo?.updatedBy}
              updatedAt={alarmPopupInfo?.updatedAt}
            />
          </Box>
          {acknowledged ? (
            <Button
              onClick={onAcknowledgeAndClose}
              variant="outlined"
              data-sm="acknowledge-and-close-button"
            >
              Acknowledge & Close
            </Button>
          ) : (
            <Button onClick={onHandleClose} variant="outlined" data-sm="close-button">
              Close
            </Button>
          )}
        </FooterStack>
      </DialogActions>
      <AlertDialogDeleteAlarmRule
        dialogTitle="Delete Alarm Event"
        AlarmRuleDeleteToggleState={state}
        onDeleteAlarmRuleToggle={onToggle}
        handleDelete={() => {
          handleDeleteAlarmEvent({
            customerId: customerId,
            siteId: siteId,
            alarmId: alarmPopupInfo?.id,
          });
          onToggle();
        }}
        title={alarmPopupInfo?.title || ''}
        isAlarmRule={false}
      />
    </AlarmPopupDialog>
  );
};
